import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import InAppMessagesPage from './components/InAppMessages/InAppMessagesPage';
import CommentsPage from './components/Comments/CommentsPage';
import CommentSettingsPage from './components/Comments/CommentSettingsPage';
import TALocalsPage from './components/TALocals/TALocalsPage';
import MediaPage from './components/Media/MediaPage';
import TidePointsPage from './components/TidePoints/TidePointsPage';
import SurveyDataPage from './components/SurveyData/SurveyDataPage';
import UserManagementPage from './components/UserManagement/UserManagementPage';
import VolunteerEventsPage from './components/VolunteerEvents/VolunteerEventsPage';
import ObservationsPage from './components/Observations/ObservationsPage';
import { AuthProviderBFF, useAuth } from './components/auth/AuthProviderBFF';
import { UserProfileBFF } from './components/auth/UserProfileBFF';
import './App.css';

function App() {
  return (
    <AuthProviderBFF>
      <Router>
        <div className="App">
          <div className="fixed top-0 right-0 p-4 z-50">
            <UserProfileBFF />
          </div>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/messages" element={<InAppMessagesPage />} />
            <Route path="/comments" element={<CommentsPage />} />
            <Route path="/comment-settings" element={<CommentSettingsPage />} />
            <Route path="/ta-locals" element={<TALocalsPage />} />
            <Route path="/media" element={<MediaPage />} />
            <Route path="/tide-points" element={<TidePointsPage />} />
            <Route path="/survey-data" element={<SurveyDataPage />} />
            <Route path="/user-management" element={<UserManagementPage />} />
            <Route path="/volunteer-events" element={<VolunteerEventsPage />} />
            <Route path="/observations" element={<ObservationsRoute />} />
          </Routes>
        </div>
      </Router>
    </AuthProviderBFF>
  );
}

// Trail observations carry hiker identity and precise positions, so the page is
// kept off-screen for anyone outside TrailApp-Admin-Observations rather than
// letting it mount and fill with 403s. Permissions are all-false until
// /api/auth/user answers, so hold the render while loading — redirecting during
// that window would bounce authorised admins on a hard refresh.
function ObservationsRoute() {
  const { permissions, loading } = useAuth();

  if (loading) {
    return null;
  }

  return permissions.trailObservations ? <ObservationsPage /> : <Navigate to="/" replace />;
}

function HomePage() {
  const { permissions } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-8 mb-8 text-white text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
              Welcome to The Trail App Admin Portal
            </h1>
            <p className="text-emerald-100 text-xl md:text-2xl font-light leading-relaxed">
              Administrative dashboard for content management and user moderation
            </p>
          </div>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
          {permissions.inAppMessages && (
            <Link to="/messages" className="block h-full min-h-[200px]">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:-translate-y-1 h-full flex flex-col min-h-[200px] text-left">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-3">In-App Messages</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4 flex-1">Create and manage messages for app users</p>
                <div className="flex items-center text-sm text-emerald-600">
                  <span className="font-medium">Manage Messages</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          )}

          {permissions.comments && (
            <Link to="/comments" className="block h-full min-h-[200px]">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:-translate-y-1 h-full flex flex-col min-h-[200px] text-left">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-3">Comment Management</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4 flex-1">View, moderate, and manage user comments</p>
                <div className="flex items-center text-sm text-emerald-600">
                  <span className="font-medium">Moderate Comments</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          )}

          {permissions.commentSettings && (
            <Link to="/comment-settings" className="block h-full min-h-[200px]">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:-translate-y-1 h-full flex flex-col min-h-[200px] text-left">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-3">Comment Settings</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4 flex-1">Configure comment visibility for features</p>
                <div className="flex items-center text-sm text-emerald-600">
                  <span className="font-medium">Configure Settings</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          )}

          {permissions.volunteerEvents && (
            <Link to="/volunteer-events" className="block h-full min-h-[200px]">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:-translate-y-1 h-full flex flex-col min-h-[200px] text-left">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-lime-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-lime-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-3">Volunteer Events</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4 flex-1">Create work parties, manage capacity and rosters</p>
                <div className="flex items-center text-sm text-emerald-600">
                  <span className="font-medium">Manage Events</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          )}

          {permissions.taLocals && (
            <Link to="/ta-locals" className="block h-full min-h-[200px]">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:-translate-y-1 h-full flex flex-col min-h-[200px] text-left">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-3">TA Locals</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4 flex-1">Manage local business partners and POIs</p>
                <div className="flex items-center text-sm text-emerald-600">
                  <span className="font-medium">Manage Locals</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          )}

          {permissions.taLocals && (
            <Link to="/media" className="block h-full min-h-[200px]">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:-translate-y-1 h-full flex flex-col min-h-[200px] text-left">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-3">Media</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4 flex-1">Browse stored images and remove them by account or listing</p>
                <div className="flex items-center text-sm text-emerald-600">
                  <span className="font-medium">Manage Media</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          )}

          {permissions.tidePoints && (
            <Link to="/tide-points" className="block h-full min-h-[200px]">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:-translate-y-1 h-full flex flex-col min-h-[200px] text-left">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-3">Tide Points</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4 flex-1">Manage ocean tide monitoring points for the mobile app</p>
                <div className="flex items-center text-sm text-emerald-600">
                  <span className="font-medium">Manage Tide Points</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          )}

          {permissions.analytics && (
            <Link to="/survey-data" className="block h-full min-h-[200px]">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:-translate-y-1 h-full flex flex-col min-h-[200px] text-left">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-3">Survey Data Analytics</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4 flex-1">Analyze trail section survey responses and improve time estimates</p>
                <div className="flex items-center text-sm text-emerald-600">
                  <span className="font-medium">View Survey Data</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          )}

          {permissions.trailObservations && (
            <Link to="/observations" className="block h-full min-h-[200px]">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:-translate-y-1 h-full flex flex-col min-h-[200px] text-left">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-3">Trail Observations</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4 flex-1">Review hiker reports of track conditions, hazards, and opportunities</p>
                <div className="flex items-center text-sm text-emerald-600">
                  <span className="font-medium">View Observations</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          )}

          {permissions.userManagement && (
            <Link to="/user-management" className="block h-full min-h-[200px]">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:-translate-y-1 h-full flex flex-col min-h-[200px] text-left">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-3">User Management</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4 flex-1">Manage feature access and beta testing for app users</p>
                <div className="flex items-center text-sm text-emerald-600">
                  <span className="font-medium">Manage Users</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          )}

          {permissions.contentModeration && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 opacity-60 h-full flex flex-col min-h-[200px] text-left">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <div className="ml-3 flex items-center">
                  <h3 className="text-lg font-semibold text-gray-500">Content Moderation</h3>
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    Coming Soon
                  </span>
                </div>
              </div>
              <p className="text-gray-400 text-sm mb-4 flex-1">Advanced content moderation tools</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
